import React from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

const NAVY = "#1B1F3B";
const YELLOW = "#FFD93D";
const CORAL = "#FF6B6B";
const TEAL = "#06D6A0";

/* Fun animated school bus SVG */
const BusSVG = () => (
  <svg width="120" height="70" viewBox="0 0 120 70" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="14" width="106" height="42" rx="8" fill={YELLOW} stroke={NAVY} strokeWidth="3" />
    <rect x="4" y="14" width="22" height="42" rx="8" fill={CORAL} stroke={NAVY} strokeWidth="3" />
    <rect x="12" y="22" width="10" height="12" rx="3" fill="#fff" stroke={NAVY} strokeWidth="2" />
    <rect x="32" y="22" width="16" height="14" rx="3" fill="#C8F5FF" stroke={NAVY} strokeWidth="2" />
    <rect x="54" y="22" width="16" height="14" rx="3" fill="#C8F5FF" stroke={NAVY} strokeWidth="2" />
    <rect x="76" y="22" width="16" height="14" rx="3" fill="#C8F5FF" stroke={NAVY} strokeWidth="2" />
    <rect x="96" y="24" width="12" height="10" rx="3" fill={TEAL} stroke={NAVY} strokeWidth="2" />
    <circle cx="28" cy="58" r="10" fill={NAVY} />
    <circle cx="28" cy="58" r="5" fill="#888" />
    <circle cx="88" cy="58" r="10" fill={NAVY} />
    <circle cx="88" cy="58" r="5" fill="#888" />
    <rect x="102" y="22" width="8" height="6" rx="2" fill={YELLOW} stroke={NAVY} strokeWidth="1.5" />
    <text x="38" y="52" fontFamily="Fredoka One, cursive" fontSize="9" fill={NAVY} fontWeight="bold">SCHOOL BUS</text>
  </svg>
);

/* Dotted road */
const RoadSVG = () => (
  <svg width="200" height="16" viewBox="0 0 200 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="6" width="200" height="4" fill={NAVY} rx="2" />
    {[0, 30, 60, 90, 120, 150].map((x) => (
      <rect key={x} x={x + 10} y="8" width="16" height="2" fill="#fff" rx="1" />
    ))}
  </svg>
);

const LoadingScreen = () => (
  <AnimatePresence>
    <motion.div
      key="loading"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.5 }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        backgroundColor: "#FFF8F0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Stars */}
      {["⭐", "🌟", "✨"].map((s, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${20 + i * 22}%`,
            left: `${10 + i * 30}%`,
            fontSize: "1.8rem",
            animation: `wiggle ${1.2 + i * 0.4}s ease-in-out infinite`,
          }}
        >
          {s}
        </Box>
      ))}

      {/* School label */}
      <Typography
        sx={{
          fontFamily: "'Fredoka One', cursive",
          fontSize: { xs: "1.8rem", md: "2.4rem" },
          color: NAVY,
          mb: 1,
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      >
        St Lord's International Public School
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: "0.9rem",
          color: CORAL,
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          mb: 5,
        }}
      >
        Bangalore
      </Typography>

      {/* Animated bus + road */}
      <Box sx={{ position: "relative", width: 240, mb: 4 }}>
        <Box
          sx={{
            animation: "floatY 1.8s ease-in-out infinite",
            position: "relative",
            zIndex: 2,
          }}
        >
          <BusSVG />
        </Box>
        <Box sx={{ mt: -1 }}>
          <RoadSVG />
        </Box>
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          width: 200,
          height: 10,
          borderRadius: 999,
          border: `2px solid ${NAVY}`,
          overflow: "hidden",
          backgroundColor: "#fff",
          boxShadow: `3px 3px 0 ${NAVY}`,
        }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: "100%" }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          style={{ height: "100%", backgroundColor: CORAL, borderRadius: 999 }}
        />
      </Box>
      <Typography
        sx={{
          mt: 2,
          fontFamily: "'Nunito', sans-serif",
          fontWeight: 800,
          fontSize: "0.85rem",
          color: NAVY,
          opacity: 0.6,
        }}
      >
        Getting ready for school… 🎒
      </Typography>
    </motion.div>
  </AnimatePresence>
);

export default LoadingScreen;
